import {Combobox, ComboboxProps} from '@cypro/ui/components/form/combobox'
import {SelectItem} from '@cypro/ui/components/form/select'
import {useGetPrescribedNames} from './api/use-get-prescribed-names'
import {usePrescribedNameWithBatches} from '@/modules/production/api/use-get-prescribed-name-with-batches'
import {mapPrescribedNamesToSelectItems, mapPrescribedNamesToSelectItemsWithCount} from './functions'
import {useTranslate} from '@tolgee/react'
import {useEffect} from 'react'

/**
 * Select items for prescribed (trade-name) product pickers.
 * With an active-substance id the list only holds products for that substance,
 * enriched with in-stock counts; without one it lists all active prescribed names.
 * Products whose trade names are all inactive are never offered (CY-259).
 */
export function usePrescribedNameSelectItems(drugCatalogItemId?: string | null) {
  const {t} = useTranslate('common')
  const isFiltered = Boolean(drugCatalogItemId)

  const [namesWithBatches] = usePrescribedNameWithBatches(drugCatalogItemId)
  const {prescribedNames: allNames} = useGetPrescribedNames({filters: {withActiveTradeName: true}})

  // with a substance filter the items come with per-product stock counts,
  // otherwise the plain active-name list is used
  const itemsWithCount = isFiltered
    ? mapPrescribedNamesToSelectItemsWithCount(namesWithBatches || []).map(item => ({
        ...item,
        description: t('items_available', {count: item.count}),
      }))
    : null

  const items: SelectItem[] = itemsWithCount
    ? itemsWithCount.map(({label, value, description}) => ({label, value, description}))
    : mapPrescribedNamesToSelectItems(allNames || [])

  const inStockItems = itemsWithCount?.filter(item => item.count > 0) ?? null
  const noItemsInStock = Boolean(inStockItems) && items.length > 0 && inStockItems?.length === 0

  return {items, inStockItems, noItemsInStock}
}

interface PrescribingFormApi {
  getFieldValue(name: string): unknown
  setFieldValue(name: string, value: unknown, options?: {dontRunListeners?: boolean; dontUpdateMeta?: boolean}): void
}

// quick validation: with exactly one product in stock it is selected automatically,
// with several the pharmacist chooses, with none the field stays empty and blocks validation
export function useAutoSelectSingleInStockPrescribedName(
  form: PrescribingFormApi,
  index: number,
  inStockItems: readonly {value: string}[],
  enabled: boolean
) {
  useEffect(() => {
    if (!enabled || inStockItems.length !== 1) return
    const currentValue = form.getFieldValue(`medicine[${index}].prescribedNameId`)
    if (!currentValue) {
      form.setFieldValue(`medicine[${index}].prescribedNameId`, inStockItems[0].value, {
        dontRunListeners: true,
        dontUpdateMeta: true,
      })
    }
  }, [form, inStockItems, index, enabled])
}

/**
 * Prescribed trade name picker for any form — render inside a `form.AppField`
 * so the shared Combobox picks up the surrounding field context.
 */
export function PrescribedTradeNameCombobox(props: ComboboxProps) {
  return <Combobox size="sm" {...props} />
}
